import React, { useState } from 'react';
import { ideaForm as ideaFormData, companyInfo } from '../data/mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Sparkles, Lightbulb, Rocket, Target, TrendingUp } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const Ideas = () => {
  const { toast } = useToast();
  const [ideaForm, setIdeaForm] = useState({
    name: '',
    email: '',
    phone: '',
    ideaTitle: '',
    ideaDescription: '',
    industry: '',
    lookingFor: ''
  });

  const handleIdeaSubmit = (e) => {
    e.preventDefault();
    // Mock submission
    toast({
      title: "Idea Submitted!",
      description: "Thank you for sharing your vision. Our team will review it carefully.",
    });
    setIdeaForm({
      name: '',
      email: '',
      phone: '',
      ideaTitle: '',
      ideaDescription: '',
      industry: '',
      lookingFor: ''
    });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-900 via-purple-900 to-black">
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 20% 50%, rgba(147, 51, 234, 0.3) 0%, transparent 50%),
                            radial-gradient(circle at 80% 80%, rgba(147, 51, 234, 0.3) 0%, transparent 50%),
                            radial-gradient(circle at 40% 20%, rgba(147, 51, 234, 0.2) 0%, transparent 50%)`
          }}></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full mb-8">
            <Sparkles className="w-4 h-4 text-purple-300" />
            <span className="text-sm text-white/90">Innovation Starts Here</span>
          </div>
          
          <h1 className="text-6xl md:text-8xl font-extralight text-white mb-8 tracking-tight">
            Shape the Future
            <br />
            <span className="text-purple-300">With AZB Solutions</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-white/80 max-w-3xl mx-auto mb-6 font-light leading-relaxed">
            Innovation begins with a single idea - and at AZB Solutions, we believe every bold vision deserves to be heard.
          </p>
          
          <p className="text-lg text-white/70 max-w-2xl mx-auto mb-12 font-light">
            We don't just build technology; we build partnerships with the dreamers shaping tomorrow.
          </p>

          {/* Scroll Indicator */}
          <div className="mt-16">
            <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2 mx-auto">
              <div className="w-1.5 h-3 bg-white/60 rounded-full animate-bounce"></div>
            </div>
          </div>
        </div>
      </section>

      {/* A Home for Bold Thinkers */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-5xl md:text-6xl font-light text-black mb-6 tracking-tight">
                A Home for
                <br />
                <span className="text-purple-600">Bold Thinkers</span>
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Dubai is where ideas come to life, and AZB Solutions is where they grow.
              </p>
              <p className="text-base text-gray-600 leading-relaxed">
                We believe the best innovations are born in collaboration. Whether you're an engineer, designer, entrepreneur, or dreamer - your vision can become the next global breakthrough.
              </p>
            </div>
            <div className="relative h-[500px] rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c"
                alt="Collaboration"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-purple-900/40 to-transparent"></div>
            </div>
          </div>
        </div>
      </section>

      {/* We Support and Invest */}
      <section className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-light text-black mb-6 tracking-tight">
              We Support and Invest in
              <br />
              <span className="text-purple-600">Visionaries</span>
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              At AZB Solutions, we don't just listen - we invest.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            <div className="bg-white p-10 rounded-3xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-2xl font-light mb-4">Technical Expertise</h3>
              <p className="text-gray-600 leading-relaxed">
                Access to world-class AI and SaaS development teams who turn concepts into scalable platforms.
              </p>
            </div>
            <div className="bg-white p-10 rounded-3xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mb-6">
                <Lightbulb className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-2xl font-light mb-4">Mentorship</h3>
              <p className="text-gray-600 leading-relaxed">
                Guidance from seasoned entrepreneurs and technology leaders who have built global companies.
              </p>
            </div>
            <div className="bg-white p-10 rounded-3xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mb-6">
                <TrendingUp className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-2xl font-light mb-4">Funding Opportunities</h3>
              <p className="text-gray-600 leading-relaxed">
                Investment and resources to help you transform bold ideas into world-class solutions.
              </p>
            </div>
          </div>

          <p className="text-center text-lg text-gray-700 mt-16 max-w-3xl mx-auto leading-relaxed">
            Our mission is to empower creative minds by providing the tools, network, and capital needed to bring revolutionary ideas to life.
          </p>
        </div>
      </section>

      {/* From Concept to Impact */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="relative h-[500px] rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1677442136019-21780ecad995"
                alt="AI Innovation"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
            </div>
            <div>
              <h2 className="text-5xl md:text-6xl font-light text-black mb-6 tracking-tight">
                From Concept
                <br />
                <span className="text-purple-600">to Impact</span>
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Every revolutionary product starts as a spark of imagination.
              </p>
              <p className="text-base text-gray-600 leading-relaxed mb-8">
                If your idea has the potential to solve real problems, inspire communities, or change how the world works - we're here to help you make it real.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-2 h-2 bg-purple-600 rounded-full mt-2"></div>
                  <p className="text-gray-700">Transform abstract concepts into concrete solutions</p>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-2 h-2 bg-purple-600 rounded-full mt-2"></div>
                  <p className="text-gray-700">Build technology that matters to people and communities</p>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-2 h-2 bg-purple-600 rounded-full mt-2"></div>
                  <p className="text-gray-700">Scale your vision to reach a global audience</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How to Connect */}
      <section className="py-32 bg-gradient-to-br from-purple-600 via-purple-700 to-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full mb-8">
            <Rocket className="w-4 h-4 text-purple-300" />
            <span className="text-sm text-white/90">Ready to Begin?</span>
          </div>
          
          <h2 className="text-5xl md:text-6xl font-light mb-6 tracking-tight">
            Share Your Vision
          </h2>
          
          <p className="text-xl text-white/80 mb-12 font-light max-w-2xl mx-auto">
            Tell us your idea, your dream, or your plan - and let's shape the future together.
          </p>
          
          <Button
            onClick={() => {
              document.getElementById('idea-form').scrollIntoView({ behavior: 'smooth' });
            }}
            size="lg"
            className="bg-white text-purple-700 hover:bg-white/90 text-base px-8 py-6 rounded-full transition-all hover:scale-105"
          >
            <Lightbulb className="mr-2 w-5 h-5" />
            Submit Your Idea
          </Button>
        </div>
      </section>

      {/* Idea Submission Form */}
      <section id="idea-form" className="py-32 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-full mb-6">
              <Lightbulb className="w-4 h-4" />
              <span className="text-sm font-medium">Share Your Vision</span>
            </div>
            <h2 className="text-5xl font-light mb-4 tracking-tight">
              Submit Your <span className="text-purple-600">Idea</span>
            </h2>
            <p className="text-lg text-gray-600">
              Have an innovative idea? We want to hear it. We support and invest in bold visions.
            </p>
          </div>

          <form onSubmit={handleIdeaSubmit} className="bg-gray-50 p-10 rounded-3xl shadow-lg">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <Label htmlFor="idea-name" className="text-gray-700 mb-2 block">Name *</Label>
                <Input
                  id="idea-name"
                  type="text"
                  required
                  value={ideaForm.name}
                  onChange={(e) => setIdeaForm({ ...ideaForm, name: e.target.value })}
                  className="rounded-xl border-gray-300 focus:border-purple-600 focus:ring-purple-600"
                  placeholder="Your name"
                />
              </div>
              <div>
                <Label htmlFor="idea-email" className="text-gray-700 mb-2 block">Email *</Label>
                <Input
                  id="idea-email"
                  type="email"
                  required
                  value={ideaForm.email}
                  onChange={(e) => setIdeaForm({ ...ideaForm, email: e.target.value })}
                  className="rounded-xl border-gray-300 focus:border-purple-600 focus:ring-purple-600"
                  placeholder="your.email@example.com"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <Label htmlFor="idea-phone" className="text-gray-700 mb-2 block">Phone</Label>
                <Input
                  id="idea-phone"
                  type="tel"
                  value={ideaForm.phone}
                  onChange={(e) => setIdeaForm({ ...ideaForm, phone: e.target.value })}
                  className="rounded-xl border-gray-300 focus:border-purple-600 focus:ring-purple-600"
                  placeholder="+1 234 567 8900"
                />
              </div>
              <div>
                <Label htmlFor="idea-industry" className="text-gray-700 mb-2 block">Industry</Label>
                <Input
                  id="idea-industry"
                  type="text"
                  value={ideaForm.industry}
                  onChange={(e) => setIdeaForm({ ...ideaForm, industry: e.target.value })}
                  className="rounded-xl border-gray-300 focus:border-purple-600 focus:ring-purple-600"
                  placeholder="e.g., FinTech, HealthTech"
                />
              </div>
            </div>

            <div className="mb-6">
              <Label htmlFor="idea-title" className="text-gray-700 mb-2 block">Idea Title *</Label>
              <Input
                id="idea-title"
                type="text"
                required
                value={ideaForm.ideaTitle}
                onChange={(e) => setIdeaForm({ ...ideaForm, ideaTitle: e.target.value })}
                className="rounded-xl border-gray-300 focus:border-purple-600 focus:ring-purple-600"
                placeholder="Give your idea a compelling title"
              />
            </div>

            <div className="mb-6">
              <Label htmlFor="idea-description" className="text-gray-700 mb-2 block">Idea Description *</Label>
              <Textarea
                id="idea-description"
                required
                rows={8}
                value={ideaForm.ideaDescription}
                onChange={(e) => setIdeaForm({ ...ideaForm, ideaDescription: e.target.value })}
                className="rounded-xl border-gray-300 focus:border-purple-600 focus:ring-purple-600 resize-none"
                placeholder="Describe your idea in detail. What problem does it solve? Who is it for? What makes it unique?"
              />
            </div>

            <div className="mb-8">
              <Label htmlFor="idea-looking-for" className="text-gray-700 mb-2 block">What Are You Looking For?</Label>
              <Textarea
                id="idea-looking-for"
                rows={4}
                value={ideaForm.lookingFor}
                onChange={(e) => setIdeaForm({ ...ideaForm, lookingFor: e.target.value })}
                className="rounded-xl border-gray-300 focus:border-purple-600 focus:ring-purple-600 resize-none"
                placeholder="e.g., Investment, Technical Partnership, Mentorship, Development Support"
              />
            </div>

            <Button
              type="submit"
              size="lg"
              className="w-full bg-purple-600 hover:bg-purple-700 text-white rounded-xl transition-all hover:scale-[1.02]"
            >
              <Lightbulb className="mr-2 w-5 h-5" />
              Submit Your Idea
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Ideas;